/**
 * PricingModal - Conversion modal for credit bundle/subscription selection
 * 
 * SIMPLIFIED PURCHASE FLOW:
 * 1. User selects a product (bundle or subscription)
 * 2. CHECK if billing profile has payment method 
 *    - If NO: Show CardCaptureModal to vault a card (auto-upgrades to A La Carte)
 *    - If YES: Proceed to confirmation
 * 3. Proceed to confirmation - engagement level is IMPLICIT based on membership type
 * 4. Use stored card for checkout
 * 
 * ENGAGEMENT LEVEL IS IMPLICIT:
 * - No payment method = Free/Introductory
 * - Has payment method + no subscription = A La Carte
 * - Has subscription = Subscriber (can only upgrade, never downgrade)
 * 
 * SECURITY: Amount determined server-side from catalog.
 */
import React, { useState, useEffect } from 'react';
import { X, CreditCard, Zap, Crown, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useReaderFunnel } from '@/contexts/ReaderFunnelContext';
import { useReaderCredits } from '@/contexts/ReaderCreditContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNotification } from '@/contexts/NotificationContext';
import PurchaseConfirmationModal from './PurchaseConfirmationModal';
import CardCaptureModal from './CardCaptureModal';
import { getBillingProfileStatus } from '@/services/billingService';
import { API_URL } from '@/config/api';

// Map legacy DB bundle_code → NFM catalog bundle_id for checkout
const LEGACY_TO_NFM_BUNDLE = {
  'starter': 'booster_pack',
  'mega': 'power_pack',
  'value': 'value_pack',
  'pro_pack': 'pro_pack',
};

export default function PricingModal() {
  const { showPricingModal, setShowPricingModal, closeAllModals } = useReaderFunnel();
  const { refreshCreditsSummary } = useReaderCredits();
  const { user, token, loadCurrentUser } = useAuth();
  const { notifyError, notifyBilling, notifyInfo } = useNotification();
  
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('bundles');  // 'bundles' | 'subscriptions'
  
  // Gate flow state
  const [currentStep, setCurrentStep] = useState('select'); // 'select' | 'card' | 'confirm' | 'checkout'
  const [pendingOfferData, setPendingOfferData] = useState(null);
  const [purchaseDetails, setPurchaseDetails] = useState(null);
  
  // Sub-modal states
  const [showCardCaptureModal, setShowCardCaptureModal] = useState(false);
  const [confirmationStage, setConfirmationStage] = useState(null);
  
  // User state
  const membershipType = user?.membership_type || 'Free';
  const isSubscriber = membershipType === 'Subscriber';
  
  // Fetch pricing from the same source as the Pricing page (/api/pricing/public)
  // so that displayed prices and RC totals are always aligned.
  useEffect(() => {
    const fetchPricing = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/pricing/public`);
        if (response.ok) {
          const data = await response.json();
          
          // Transform legacy DB format → modal display format
          const transformedPricing = {
            credit_bundles: (data.credit_bundles || []).map(bundle => ({
              id: bundle.id || bundle.bundle_code,
              bundle_code: bundle.bundle_code,
              // Resolve NFM bundle ID for checkout
              nfm_bundle_id: LEGACY_TO_NFM_BUNDLE[bundle.bundle_code] || bundle.bundle_code,
              bundle_name: bundle.bundle_name,
              credit_quantity: bundle.credit_quantity,
              // A La Carte price (shown to non-subscribers)
              price_usd: bundle.a_la_carte_price || bundle.price_usd || 0,
              // Subscriber price (shown to subscribers) — matches PricingPage
              subscriber_price_usd: bundle.price_usd || 0,
              badge: bundle.badge || null
            })),
            subscription_tiers: (data.subscription_tiers || []).map(tier => ({
              id: tier.id || tier.tier_code,
              tier_code: tier.tier_code,
              tier_name: tier.tier_name,
              monthly_price_usd: tier.monthly_price_usd || 0,
              annual_price_usd: tier.annual_price_usd || null,
              included_reader_credits: tier.included_reader_credits || 0
            }))
          };
          
          setPricing(transformedPricing);
        }
      } catch (error) {
        console.error('[PRICING] Failed to fetch pricing:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (showPricingModal) {
      fetchPricing();
      setCurrentStep('select');
      setPendingOfferData(null);
      setConfirmationStage(null);
    }
  }, [showPricingModal, membershipType]);
  
  // Early return if nothing to show
  if (!showPricingModal && !confirmationStage && !showCardCaptureModal) return null;
  
  // ============================================================================
  // STEP 1: User selects an offer - triggers payment method check FIRST
  // ============================================================================
  const handleSelectOffer = async (offerData) => {
    setPendingOfferData(offerData);
    setPurchaseDetails({
      type: offerData.billingCycle ? `subscription_${offerData.billingCycle}` : 'bundle',
      name: offerData.name,
      credits: offerData.credits,
      price: offerData.price,
      isAnnual: offerData.billingCycle === 'annual',
      tierCode: offerData.tierCode
    });
    
    setProcessing(true);
    
    try {
      // CHECK: Payment Method (required for ALL purchases)
      console.log('[PRICING] Checking billing profile for payment method...');
      const billingStatus = await getBillingProfileStatus(user?.id, token);
      
      if (!billingStatus.has_payment_method) {
        // No payment method - show card capture modal FIRST
        // After adding card, user becomes A La Carte automatically
        console.log('[PRICING] No payment method - showing card capture modal');
        setShowCardCaptureModal(true);
        setProcessing(false);
        return;
      }
      
      // Payment method exists - proceed directly to confirmation
      // Engagement level is IMPLICIT based on current membership:
      // - Has payment method + no subscription = A La Carte
      // - Has subscription = Subscriber (can only upgrade, never downgrade)
      console.log('[PRICING] Payment method verified - proceeding to confirmation');
      console.log('[PRICING] Current membership:', membershipType);
      console.log('[PRICING] Target:', offerData.targetMembershipType);
      
      setCurrentStep('confirm');
      setConfirmationStage('confirm');
      setShowPricingModal(false);
      
    } catch (error) {
      console.error('[PRICING] Gate check error:', error);
      // If checks fail, try to proceed to confirmation anyway (graceful degradation)
      setCurrentStep('confirm');
      setConfirmationStage('confirm');
      setShowPricingModal(false);
    } finally {
      setProcessing(false);
    }
  };
  
  // ============================================================================
  // CARD CAPTURE CALLBACK: Payment method added successfully
  // ============================================================================
  const handleCardCaptureSuccess = async (result) => {
    console.log('[PRICING] Card captured successfully:', result);
    setShowCardCaptureModal(false);
    
    // Refresh user data to get updated membership type (now A La Carte)
    await loadCurrentUser?.();
    
    // Card added = User is now A La Carte
    // Go straight to confirmation for ALL purchases (bundles or subscriptions)
    console.log('[PRICING] Card added - user is now A La Carte, proceeding to confirmation');
    setCurrentStep('confirm');
    setConfirmationStage('confirm');
  };
  
  // ============================================================================
  // CARD CAPTURE CANCEL: User cancelled card capture
  // ============================================================================
  const handleCardCaptureCancel = () => {
    setShowCardCaptureModal(false);
    // Return to pricing modal
    setShowPricingModal(true);
    setPendingOfferData(null);
    setPurchaseDetails(null);
  };
  
  // ============================================================================
  // STEP 3: User confirms purchase - use authorization or redirect to checkout
  // ============================================================================
  const handleConfirmPurchase = async () => {
    if (!pendingOfferData) return;
    
    const {
      offerId,
      targetMembershipType,
      credits,
      price,
      name,
      billingCycle,
      tierCode
    } = pendingOfferData;
    
    setProcessing(true);
    
    try {
      // Determine product type and ID for NFM checkout
      const isSubscription = targetMembershipType === 'Subscriber';
      const productType = isSubscription ? 'subscription' : 'bundle';
      
      // Map legacy offer IDs to NFM product IDs
      let productId = offerId;
      if (isSubscription) {
        // Convert tier_code from hyphenated format (pro-reader) to underscore format (pro_reader)
        const normalizedTierCode = (tierCode || offerId).replace(/-/g, '_');
        productId = `${normalizedTierCode}_${billingCycle || 'monthly'}`;
      }
      
      // User is subscriber if their membership type is 'Subscriber'
      const userIsSubscriber = isSubscriber;
      
      // FIRST: Check if user has stored payment method for authorization-based checkout
      console.log('[PRICING] Checking for stored payment method...');
      const authCheckResponse = await fetch(`${API_URL}/api/nfm/checkout/authorize/check?user_id=${user?.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_type: productType,
          product_id: productId,
          is_subscriber: userIsSubscriber
        })
      });
      
      if (authCheckResponse.ok) {
        const authCheck = await authCheckResponse.json();
        
        if (authCheck.can_authorize && authCheck.use_stored_card) {
          // User has stored payment method - use authorization-based checkout
          console.log('[PRICING] Using stored payment method:', authCheck);
          
          // Execute the authorization charge
          const executeResponse = await fetch(`${API_URL}/api/nfm/checkout/authorize/execute?user_id=${user?.id}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              product_type: productType,
              product_id: productId,
              is_subscriber: userIsSubscriber,
              user_confirmed: true
            })
          });
          
          const executeResult = await executeResponse.json();
          
          if (executeResult.status === 'success') {
            // SUCCESS! Show success modal
            console.log('[PRICING] Authorization charge successful:', executeResult);
            notifyBilling(`Purchase complete! ${executeResult.credits_issued} credits added.`);
            
            // Refresh user data
            await loadCurrentUser();
            await refreshCreditsSummary();
            
            // Show success in confirmation modal
            setConfirmationStage('success');
            setPurchaseDetails(prev => ({
              ...prev,
              creditsIssued: executeResult.credits_issued,
              newBalance: executeResult.new_balance,
              transactionId: executeResult.transaction_id,
              subscriptionStartDate: executeResult.subscription_start_date,
              subscriptionEndDate: executeResult.subscription_end_date
            }));
            setProcessing(false);
            return;
          } else if (executeResult.fallback_to_checkout) {
            // Authorization failed - fall back to Stripe checkout
            console.log('[PRICING] Authorization failed, falling back to checkout:', executeResult.message);
            // Silent fallback - checkout handles the flow
          } else {
            notifyError(executeResult.message || 'Payment failed');
            handleReturnToOptions();
            return;
          }
        }
      }
      
      // FALLBACK: Use standard Stripe checkout
      console.log('[PRICING] Creating NFM checkout session:', {
        productType,
        productId,
        userIsSubscriber,
        userId: user?.id
      });
      
      // Create NFM checkout session
      const response = await fetch(`${API_URL}/api/nfm/checkout/session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: user?.id,
          product_type: productType,
          product_id: productId,
          is_subscriber: userIsSubscriber,
          origin_url: window.location.origin
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Redirect to Stripe checkout
        if (data.checkout_url) {
          console.log('[PRICING] Redirecting to Stripe checkout:', data.checkout_url);
          window.location.href = data.checkout_url;
        } else {
          notifyError('Checkout URL not available');
          handleReturnToOptions();
        }
      } else {
        const error = await response.json();
        notifyError(error.detail || 'Failed to create checkout session');
        handleReturnToOptions();
      }
    } catch (error) {
      console.error('[PRICING] Checkout error:', error);
      notifyError('Failed to initiate checkout');
      handleReturnToOptions();
    } finally {
      setProcessing(false);
    }
  };
  
  // Cancel purchase and return to options
  const handleCancelPurchase = () => {
    setConfirmationStage('cancelled');
  };
  
  // Return to pricing options
  const handleReturnToOptions = () => {
    setConfirmationStage(null);
    setPurchaseDetails(null);
    setPendingOfferData(null);
    setCurrentStep('select');
    setShowPricingModal(true);
  };
  
  // Final close
  const handleFinalClose = () => {
    setConfirmationStage(null);
    setPurchaseDetails(null);
    setPendingOfferData(null);
    setShowCardCaptureModal(false);
    setCurrentStep('select');
    closeAllModals();
  };
  
  const handleDismiss = () => {
    setShowPricingModal(false);
  };
  
  // Sort bundles by credits (ascending)
  const sortedBundles = pricing?.credit_bundles 
    ? [...pricing.credit_bundles].sort((a, b) => (a.price_usd || 0) - (b.price_usd || 0))
    : [];
  
  // Sort subscription tiers by price (ascending)
  const sortedTiers = pricing?.subscription_tiers
    ? [...pricing.subscription_tiers].sort((a, b) => (a.monthly_price_usd || 0) - (b.monthly_price_usd || 0))
    : [];
  
  // ============================================================================
  // RENDER: Card Capture Modal (Payment Method Required)
  // ============================================================================
  if (showCardCaptureModal) {
    return (
      <CardCaptureModal
        isOpen={true}
        onClose={handleCardCaptureCancel}
        onSuccess={handleCardCaptureSuccess}
        userId={user?.id}
        userEmail={user?.email}
        userName={user?.name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim()}
        token={token}
        membershipType={membershipType}
      />
    );
  }
  
  // ============================================================================
  // RENDER: Confirmation Modal
  // ============================================================================
  if (confirmationStage) {
    return (
      <PurchaseConfirmationModal
        isOpen={true}
        onClose={confirmationStage === 'cancelled' ? handleReturnToOptions : handleFinalClose}
        onConfirm={handleConfirmPurchase}
        onCancel={handleCancelPurchase}
        purchaseDetails={purchaseDetails}
        stage={confirmationStage}
      />
    );
  }
  
  // Don't render pricing modal if not showing
  if (!showPricingModal) return null;
  
  // ============================================================================
  // RENDER: Main Pricing Modal
  // ============================================================================
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8"
      data-testid="pricing-conversion-modal"
    >
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-purple-500/30 rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
        {/* Header with Logo */}
        <div className="relative px-6 pt-6 pb-4 border-b border-slate-700 text-center">
          <button 
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-700/50 transition-colors"
            data-testid="pricing-modal-close-btn"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
          
          {/* NewAudia Logo */}
          <img 
            src="/newaudia-logo.jpg" 
            alt="NewAudia" 
            className="h-12 mx-auto mb-3 rounded-lg"
          />
          <h2 className="text-xl font-bold text-white">Choose Your Plan</h2>
          <p className="text-sm text-slate-400">Select credits or a subscription to continue your journey</p>
        </div>
        
        {/* Processing Overlay */}
        {processing && (
          <div className="absolute inset-0 bg-slate-900/80 flex items-center justify-center z-10">
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-purple-500 animate-spin mx-auto mb-2" />
              <p className="text-slate-300">Preparing checkout...</p>
            </div>
          </div>
        )}
        
        {/* Tab Navigation (only show if not subscriber) */}
        {!isSubscriber && (
          <div className="flex border-b border-slate-700">
            <button
              onClick={() => setActiveTab('bundles')}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                activeTab === 'bundles' 
                  ? 'text-purple-400 border-b-2 border-purple-400 bg-purple-500/10' 
                  : 'text-slate-400 hover:text-slate-300'
              }`}
              data-testid="tab-bundles"
            >
              <Zap className="w-4 h-4 inline mr-2" />
              Credit Bundles
            </button>
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                activeTab === 'subscriptions' 
                  ? 'text-purple-400 border-b-2 border-purple-400 bg-purple-500/10' 
                  : 'text-slate-400 hover:text-slate-300'
              }`}
              data-testid="tab-subscriptions"
            >
              <Crown className="w-4 h-4 inline mr-2" />
              Subscriptions
            </button>
          </div>
        )}
        
        {/* Content */}
        <div className="px-6 py-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Info Banner - Flow Explanation */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-300">
                  <strong className="text-white">Secure Checkout:</strong> After selection, you&apos;ll confirm your purchase and complete payment using your stored card.
                </p>
              </div>
              
              {/* Credit Bundles */}
              {(activeTab === 'bundles' || isSubscriber) && sortedBundles.length > 0 && (
                <div className="grid gap-3">
                  {isSubscriber && (
                    <div className="text-sm text-purple-300 bg-purple-900/20 rounded-lg px-3 py-2 mb-2">
                      🎉 As a subscriber, you get exclusive pricing on credit bundles!
                    </div>
                  )}
                  {sortedBundles.map((bundle) => {
                    const displayPrice = (isSubscriber && bundle.subscriber_price_usd != null)
                      ? bundle.subscriber_price_usd
                      : (bundle.price_usd || 0);
                    const hasDiscount = isSubscriber && bundle.subscriber_price_usd != null && bundle.subscriber_price_usd < bundle.price_usd;
                    
                    return (
                      <div 
                        key={bundle.bundle_code || bundle.id}
                        className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${
                          hasDiscount 
                            ? 'bg-gradient-to-r from-purple-900/30 to-pink-900/30 border-purple-500/50 hover:border-purple-400/70' 
                            : 'bg-slate-800/50 border-slate-700 hover:border-purple-500/50'
                        }`}
                        data-testid={`bundle-option-${bundle.bundle_code || bundle.id}`}
                      >
                        <div>
                          <div className="font-semibold text-white flex items-center gap-2">
                            {bundle.bundle_name}
                            {hasDiscount && (
                              <span className="text-xs bg-green-500 text-white px-1.5 py-0.5 rounded-full">
                                {Math.round((1 - bundle.subscriber_price_usd / bundle.price_usd) * 100)}% OFF
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-slate-400">
                            {hasDiscount ? (
                              <>
                                <span className="line-through text-slate-500">${bundle.price_usd.toFixed(2)}</span>
                                {' '}
                                <span className="text-purple-300 font-semibold">${displayPrice.toFixed(2)}</span>
                              </>
                            ) : (
                              <>${displayPrice.toFixed(2)}</>
                            )}
                            {' '}• {bundle.credit_quantity} RC
                          </div>
                          {bundle.badge && (
                            <div className="text-xs text-purple-400 mt-1">{bundle.badge}</div>
                          )}
                        </div>
                        <Button
                          onClick={() => handleSelectOffer({
                            offerId: bundle.nfm_bundle_id || bundle.bundle_code || bundle.id,
                            targetMembershipType: 'ALaCarte',
                            credits: bundle.credit_quantity,
                            price: displayPrice,
                            name: bundle.bundle_name,
                            billingCycle: null,
                            tierCode: null
                          })}
                          disabled={processing}
                          className="bg-purple-600 hover:bg-purple-500 text-white"
                          size="sm"
                          data-testid={`select-bundle-${bundle.bundle_code || bundle.id}`}
                        >
                          {processing ? 'Processing...' : 'Select'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Subscriptions (only for non-subscribers) */}
              {activeTab === 'subscriptions' && !isSubscriber && sortedTiers.length > 0 && (
                <div className="grid gap-3">
                  {sortedTiers.map((tier) => (
                    <div 
                      key={tier.tier_code || tier.id}
                      className="p-4 bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-xl border border-purple-500/30 hover:border-purple-400/50 transition-colors"
                      data-testid={`subscription-option-${tier.tier_code}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="font-semibold text-white text-lg">{tier.tier_name}</div>
                        <div className="text-sm text-purple-300">{tier.included_reader_credits || 0} RC/mo</div>
                      </div>
                      
                      {/* Monthly and Annual options */}
                      <div className="grid grid-cols-2 gap-2">
                        {/* Monthly Option */}
                        <Button
                          onClick={() => handleSelectOffer({
                            offerId: `${tier.tier_code}_monthly`,
                            targetMembershipType: 'Subscriber',
                            credits: tier.included_reader_credits || 0,
                            price: tier.monthly_price_usd,
                            name: `${tier.tier_name} (Monthly)`,
                            billingCycle: 'monthly',
                            tierCode: tier.tier_code
                          })}
                          disabled={processing}
                          variant="outline"
                          className="flex flex-col items-center py-3 h-auto border-purple-500/50 hover:bg-purple-600/20 text-white"
                          size="sm"
                          data-testid={`select-subscription-${tier.tier_code}-monthly`}
                        >
                          <span className="text-xs text-slate-400 mb-1">Monthly</span>
                          <span className="font-semibold">${(tier.monthly_price_usd || 0).toFixed(2)}/mo</span>
                        </Button>
                        
                        {/* Annual Option */}
                        {tier.annual_price_usd && (
                          <Button
                            onClick={() => handleSelectOffer({
                              offerId: `${tier.tier_code}_annual`,
                              targetMembershipType: 'Subscriber',
                              credits: tier.included_reader_credits || 0,
                              price: tier.annual_price_usd,
                              name: `${tier.tier_name} (Annual)`,
                              billingCycle: 'annual',
                              tierCode: tier.tier_code
                            })}
                            disabled={processing}
                            className="flex flex-col items-center py-3 h-auto bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white relative"
                            size="sm"
                            data-testid={`select-subscription-${tier.tier_code}-annual`}
                          >
                            <span className="absolute -top-2 right-1 px-1.5 py-0.5 text-[10px] bg-green-500 text-white rounded-full">
                              Save {Math.round((1 - tier.annual_price_usd / (tier.monthly_price_usd * 12)) * 100)}%
                            </span>
                            <span className="text-xs text-purple-200 mb-1">Annual</span>
                            <span className="font-semibold">${(tier.annual_price_usd || 0).toFixed(2)}/yr</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Empty state */}
              {!loading && sortedBundles.length === 0 && sortedTiers.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  No pricing options available at this time.
                </div>
              )}
            </div>
          )}
          
          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 text-center">
              🔒 Secure payment processing powered by Stripe. Your card details are never stored on our servers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
